import { requiresConductSupportingDocument, requiresOtherProfessionalQualificationText } from './formState';
import type { VolunteerFormState } from './types';

export type VolunteerValidationIssue = {
  stepIndex: number;
  field: keyof VolunteerFormState;
  messageKey: VolunteerValidationMessageKey;
};

export const volunteerValidationMessageKeys = {
  surnameEnRequired: 'pbl.lawyerRegistration.personalInformation.errors.surnameEnRequired',
  givenNameEnRequired: 'pbl.lawyerRegistration.personalInformation.errors.givenNameEnRequired',
  surnameZhRequired: 'pbl.lawyerRegistration.personalInformation.errors.surnameZhRequired',
  givenNameZhRequired: 'pbl.lawyerRegistration.personalInformation.errors.givenNameZhRequired',
  phoneRequired: 'pbl.lawyerRegistration.personalInformation.errors.phoneRequired',
  phoneInvalid: 'pbl.lawyerRegistration.personalInformation.errors.phoneInvalid',
  verifyOtp: 'pbl.lawyerRegistration.validation.verifySmsAndEmailOtp',
  emailRequired: 'pbl.lawyerRegistration.personalInformation.errors.emailRequired',
  emailInvalid: 'pbl.lawyerRegistration.personalInformation.errors.emailInvalid',
  passwordRequired: 'pbl.lawyerRegistration.personalInformation.errors.passwordRequired',
  passwordRule: 'pbl.lawyerRegistration.personalInformation.helpText.passwordRule',
  confirmPasswordRequired: 'pbl.lawyerRegistration.personalInformation.errors.confirmPasswordRequired',
  passwordMismatch: 'pbl.lawyerRegistration.personalInformation.errors.passwordMismatch',
  admissionYearRequired: 'pbl.lawyerRegistration.personalInformation.errors.admissionYearRequired',
  admissionYearInvalid: 'pbl.lawyerRegistration.personalInformation.errors.admissionYearInvalid',
  selectProfessionalQualification: 'pbl.lawyerRegistration.validation.selectProfessionalQualification',
  specifyProfessionalQualification: 'pbl.lawyerRegistration.validation.specifyProfessionalQualification',
  selectLanguage: 'pbl.lawyerRegistration.validation.selectLanguage',
  lawFirmNameRequired: 'pbl.lawyerRegistration.practiceInformation.errors.lawFirmNameRequired',
  positionRequired: 'pbl.lawyerRegistration.practiceInformation.errors.positionRequired',
  selectServiceArea: 'pbl.lawyerRegistration.validation.selectServiceArea',
  profileConsentRequired: 'pbl.lawyerRegistration.profileCv.errors.consentBeforeProceeding',
  authorisationUploadRequired: 'pbl.lawyerRegistration.authorisation.errors.uploadRequired',
  confirmAuthorisation: 'pbl.lawyerRegistration.authorisation.errors.confirmAuthorisation',
  confirmLetterAttached: 'pbl.lawyerRegistration.authorisation.errors.confirmLetterAttached',
  agreeNotifyChange: 'pbl.lawyerRegistration.authorisation.errors.agreeNotifyChange',
  servicePledgeRequired: 'pbl.lawyerRegistration.servicePledge.errors.agreementRequired',
  postCompletionRequired: 'pbl.lawyerRegistration.postCompletion.errors.agreementRequired',
  picsRequired: 'pbl.lawyerRegistration.personalInformationCollection.errors.acknowledgementRequired',
  conductAnswerRequired: 'pbl.lawyerRegistration.conductCheck.errors.selectYesNo',
  conductDocumentRequired: 'pbl.lawyerRegistration.validation.uploadConductSupportingDocument',
  signatureRequired: 'pbl.lawyerRegistration.validation.completeSignature',
} as const;

export type VolunteerValidationMessageKey =
  typeof volunteerValidationMessageKeys[keyof typeof volunteerValidationMessageKeys];

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phonePattern = /^[+\d][\d\s-]{7,19}$/;
const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{9,}$/;

const hasText = (value?: string) => Boolean(value?.trim());

const issue = (
  stepIndex: number,
  field: keyof VolunteerFormState,
  messageKey: VolunteerValidationMessageKey
): VolunteerValidationIssue => ({ stepIndex, field, messageKey });

/**
 * Finds the first invalid field in the same order as the registration steps.
 * This intentionally validates form state rather than mounted inputs because
 * applicants can now skip directly to any step from the step navigation.
 */
export const getFirstVolunteerValidationIssue = (
  form: VolunteerFormState,
  requiresAccountVerification: boolean
): VolunteerValidationIssue | null => {
  if (!hasText(form.surname_en)) return issue(0, 'surname_en', volunteerValidationMessageKeys.surnameEnRequired);
  if (!hasText(form.given_name_en)) {
    return issue(0, 'given_name_en', volunteerValidationMessageKeys.givenNameEnRequired);
  }
  if (!hasText(form.surname_zh)) return issue(0, 'surname_zh', volunteerValidationMessageKeys.surnameZhRequired);
  if (!hasText(form.given_name_zh)) {
    return issue(0, 'given_name_zh', volunteerValidationMessageKeys.givenNameZhRequired);
  }

  if (requiresAccountVerification) {
    if (!hasText(form.phone)) return issue(0, 'phone', volunteerValidationMessageKeys.phoneRequired);
    if (!phonePattern.test(form.phone.trim())) return issue(0, 'phone', volunteerValidationMessageKeys.phoneInvalid);
    if (!form.phone_otp_verified) return issue(0, 'phone', volunteerValidationMessageKeys.verifyOtp);
    if (!hasText(form.email)) return issue(0, 'email', volunteerValidationMessageKeys.emailRequired);
    if (!emailPattern.test(form.email.trim())) return issue(0, 'email', volunteerValidationMessageKeys.emailInvalid);
    if (!form.email_otp_verified) return issue(0, 'email', volunteerValidationMessageKeys.verifyOtp);
    if (!hasText(form.password)) return issue(0, 'password', volunteerValidationMessageKeys.passwordRequired);
    if (!passwordPattern.test(form.password)) {
      return issue(0, 'password', volunteerValidationMessageKeys.passwordRule);
    }
    if (!hasText(form.password_confirmation)) {
      return issue(0, 'password_confirmation', volunteerValidationMessageKeys.confirmPasswordRequired);
    }
    if (form.password_confirmation !== form.password) {
      return issue(0, 'password_confirmation', volunteerValidationMessageKeys.passwordMismatch);
    }
  }

  if (!hasText(form.hk_solicitor_admission_year)) {
    return issue(0, 'hk_solicitor_admission_year', volunteerValidationMessageKeys.admissionYearRequired);
  }

  const admissionYear = Number(form.hk_solicitor_admission_year);
  const currentYear = new Date().getFullYear();
  if (!/^\d{4}$/.test(form.hk_solicitor_admission_year) || admissionYear < 1900 || admissionYear > currentYear) {
    return issue(0, 'hk_solicitor_admission_year', volunteerValidationMessageKeys.admissionYearInvalid);
  }

  if (!form.other_professional_qualifications?.length) {
    return issue(
      0,
      'other_professional_qualifications',
      volunteerValidationMessageKeys.selectProfessionalQualification
    );
  }
  if (requiresOtherProfessionalQualificationText(form) && !hasText(form.other_professional_qualification_text)) {
    return issue(
      0,
      'other_professional_qualification_text',
      volunteerValidationMessageKeys.specifyProfessionalQualification
    );
  }
  if (!form.language_proficiency?.length) {
    return issue(0, 'language_proficiency', volunteerValidationMessageKeys.selectLanguage);
  }

  if (!hasText(form.law_firm_name)) {
    return issue(1, 'law_firm_name', volunteerValidationMessageKeys.lawFirmNameRequired);
  }
  if (!hasText(form.position_in_law_firm)) {
    return issue(1, 'position_in_law_firm', volunteerValidationMessageKeys.positionRequired);
  }

  if (!form.service_areas?.length) {
    return issue(2, 'service_areas', volunteerValidationMessageKeys.selectServiceArea);
  }

  if (!form.cv_publication_consent) {
    return issue(3, 'cv_publication_consent', volunteerValidationMessageKeys.profileConsentRequired);
  }

  if (typeof form.firm_authorisation_file_id !== 'number') {
    return issue(4, 'firm_authorisation_file_id', volunteerValidationMessageKeys.authorisationUploadRequired);
  }
  if (!form.firm_authorisation_confirmed) {
    return issue(4, 'firm_authorisation_confirmed', volunteerValidationMessageKeys.confirmAuthorisation);
  }
  if (!form.firm_authorisation_letter_attached) {
    return issue(4, 'firm_authorisation_letter_attached', volunteerValidationMessageKeys.confirmLetterAttached);
  }
  if (!form.law_firm_change_notify_agreed) {
    return issue(4, 'law_firm_change_notify_agreed', volunteerValidationMessageKeys.agreeNotifyChange);
  }

  if (!form.service_pledge_agreed) {
    return issue(5, 'service_pledge_agreed', volunteerValidationMessageKeys.servicePledgeRequired);
  }
  if (!form.post_completion_agreed) {
    return issue(6, 'post_completion_agreed', volunteerValidationMessageKeys.postCompletionRequired);
  }
  if (!form.pics_agreed) {
    return issue(7, 'pics_agreed', volunteerValidationMessageKeys.picsRequired);
  }

  const conductFields: Array<keyof VolunteerFormState> = [
    'bankrupt_or_insolvent',
    'arrangement_with_creditors',
    'criminal_conviction',
    'professional_misconduct_or_pending_disciplinary',
    'suspended_from_professional_practice',
  ];
  const unansweredConductField = conductFields.find((field) => form[field] === undefined);
  if (unansweredConductField) {
    return issue(8, unansweredConductField, volunteerValidationMessageKeys.conductAnswerRequired);
  }
  if (requiresConductSupportingDocument(form) && typeof form.conduct_supporting_document_file_id !== 'number') {
    return issue(8, 'conduct_supporting_document_file_id', volunteerValidationMessageKeys.conductDocumentRequired);
  }

  if (!hasText(form.declaration_signature)) {
    return issue(9, 'declaration_signature', volunteerValidationMessageKeys.signatureRequired);
  }
  if (!hasText(form.declaration_signed_at)) {
    return issue(9, 'declaration_signed_at', volunteerValidationMessageKeys.signatureRequired);
  }

  return null;
};
